import React, { useMemo, useState, useEffect } from "react";
import { Box, Chip, IconButton, Menu, MenuItem, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { USE_FAKE_DATA } from "../../config/appConfig";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import Pagination from "../../components/Panigation";

export type TripStatus = "ON_TIME" | "DELAYED" | "COMPLETED";

export type Trip = {
  id: string;
  bookingId: string;
  customer: string;
  project: string;
  vehicle: string;
  driver: string;
  status: TripStatus;
  lastLocation: string; // text placeholder
  speedKph: number;
  eta: string; // text placeholder
};

const FAKE_TRIPS: Trip[] = [
  { id: "t1", bookingId: "BK-10002", customer: "BuildCo", project: "Expo Villas", vehicle: "Truck-07", driver: "Ali M.", status: "ON_TIME", lastLocation: "E311 - DXB", speedKph: 62, eta: "10:45" },
  { id: "t2", bookingId: "BK-10003", customer: "Skyline", project: "Harbour Point", vehicle: "Truck-12", driver: "Vijay K.", status: "DELAYED", lastLocation: "Sheikh Zayed Rd", speedKph: 18, eta: "11:30" },
  { id: "t3", bookingId: "BK-10004", customer: "Acme LLC", project: "Marina Tower", vehicle: "Truck-03", driver: "John S.", status: "COMPLETED", lastLocation: "Site", speedKph: 0, eta: "Delivered" },
];

const statusChip = (s: TripStatus) => (
  <Chip
    size="small"
    label={s === "ON_TIME" ? "On-Time" : s === "DELAYED" ? "Delayed" : "Completed"}
    color={s === "ON_TIME" ? "success" : s === "DELAYED" ? "warning" : "default"}
    sx={{ fontSize: 12 }}
  />
);

const DeliveryTrackingPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TripStatus | "ALL">("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [items, setItems] = useState<Trip[]>(USE_FAKE_DATA ? FAKE_TRIPS : []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionForId, setActionForId] = useState<string | null>(null);

  const openMenu = Boolean(anchorEl);
  const openActions = (e: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(e.currentTarget);
    setActionForId(id);
  };
  const closeActions = () => { setAnchorEl(null); setActionForId(null); };

  const filtered = useMemo(() => {
    let data = [...items];
    if (tab !== "ALL") data = data.filter((t) => t.status === tab);
    const term = (debounced || "").toLowerCase();
    if (term) {
      data = data.filter((t) =>
        t.bookingId.toLowerCase().includes(term) ||
        t.customer.toLowerCase().includes(term) ||
        t.project.toLowerCase().includes(term)
      );
    }
    return data;
  }, [items, tab, debounced]);

  const total = filtered.length;
  const start = (page - 1) * limit;
  const end = start + limit;
  const pageItems = filtered.slice(start, end);
  const totalPages = Math.max(1, Math.ceil(total / (limit || 10)));

  useEffect(() => { setPage(1); }, [tab, debounced]);

  return (
    <>
      <div className="main_title">
        <h1>Delivery Tracking</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Delivery
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: '100%', mb: 1 }}>
              <Tabs
                value={tab === 'ALL' ? 0 : tab === 'ON_TIME' ? 1 : tab === 'DELAYED' ? 2 : 3}
                onChange={(_, i) => setTab(i === 0 ? 'ALL' : i === 1 ? 'ON_TIME' : i === 2 ? 'DELAYED' : 'COMPLETED')}
                className="custom_tabs_links"
              >
                <Tab label="All" />
                <Tab label="On-Time" />
                <Tab label="Delayed" />
                <Tab label="Completed" />
              </Tabs>
            </Box>
            <SearchBar
              searchTerm={searchTerm}
              placeholder="Search by Booking ID, Customer, Project..."
              setDebouncedSearchTerm={setDebounced}
              value={searchTerm}
              onCross={() => setSearchTerm('')}
              onChange={(val: any) => { if (isValidInput(val.target.value)) setSearchTerm(val.target.value); }}
            />
          </div>
          <div className="right_s">
            <Button className="btn btn_primary" onClick={() => setItems(FAKE_TRIPS)}>Reset</Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Last Location</TableCell>
                <TableCell>Speed</TableCell>
                <TableCell>ETA</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.length ? pageItems.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.bookingId}</TableCell>
                  <TableCell>{t.customer}</TableCell>
                  <TableCell>{t.project}</TableCell>
                  <TableCell>{t.vehicle}</TableCell>
                  <TableCell>{t.driver}</TableCell>
                  <TableCell>{t.lastLocation}</TableCell>
                  <TableCell>{t.speedKph} km/h</TableCell>
                  <TableCell>{t.eta}</TableCell>
                  <TableCell>{statusChip(t.status)}</TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => openActions(e, t.id)}>
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenu && actionForId === t.id}
                      onClose={closeActions}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => { navigate(`/delivery/${t.id}`, { state: { trip: t } }); closeActions(); }}>View</MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={10}>No trips found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Pagination module={pageItems} page={page} setPage={setPage} limit={limit} setLimit={setLimit} onPageChange={setPage} totalPages={totalPages} />
        </TableContainer>
      </div>
    </>
  );
};

export default DeliveryTrackingPage;
